import React, { useMemo, useEffect } from "react";
import { createStructuredSelector } from "reselect";
import { connect } from "react-redux";
import classNames from "classnames";
import { isEmpty } from "lodash";

import { getNetTotalEnergyConsumption } from "utils/results";
import { calcOperatingCosts } from "utils/upgrades";

import { actions as modelActions } from "features/Model/_ducks";
import { selectNzStudyReportPdfData, selectFuelPrices } from "features/Model/_ducks/selectors";
import { selectResults } from "features/Model/Review/Results/_ducks/selectors";

import { PieChartSVG, sectionColorLabel } from "../../NZReportPDF/NZReportPDFPage3";
import Input from "components/Input";
import InputSectionContainer from "components/PdfExport/InputSectionContainer";

import classes from "../../styles.module.scss";

const { updateNzStudyPdfData } = modelActions;

const EnergySummary = ({
    nzStudyReportPdfData = {},
    updateNzStudyPdfData,
    results,
    fuelPrices,
    eFileResults = {},
    eFileModelData = {},
}) => {
    // Field changes aren't implemented yet since fields are always disabled

    const {
        selectedUpgrade = "",
        operatingCostSaving = 0,
        operatingCostSavingOverride = false,
        estimatedUpfront = 0,
        carbonPayback = 0,
    } = nzStudyReportPdfData;

    // Determine if selected upgrade is from E-file
    const isEFileUpgrade = selectedUpgrade.startsWith("eFile_");
    const actualUpgradeKey = isEFileUpgrade ? selectedUpgrade.replace("eFile_", "") : selectedUpgrade;

    const upgradeCaseKey = isEFileUpgrade && actualUpgradeKey === "base" ? "socBaseCase" : "socAllUpgrades";

    // Use E-file data if selected upgrade is from E-file, otherwise use current model data
    const postRetrofitPackageResults = isEFileUpgrade ? eFileResults : results?.packageResults || {};

    const eFileBaseModelData = isEFileUpgrade && actualUpgradeKey === "base" ? eFileModelData : {};

    const postRetrofitUpgradeCaseKey = isEmpty(eFileBaseModelData) ? "socAllUpgrades" : "socBaseCase";

    const { modelData: preRetrofitModelDataFromResults = {}, resultsData: preRetrofitResultsData = {} } =
        results?.packageResults?.base || {};

    const { modelData: postRetrofitModelDataFromResults = {}, resultsData: postRetrofitResultsData = {} } =
        postRetrofitPackageResults[actualUpgradeKey] || {};

    const { annualEnergyConsumption: aecPostRetrofit = 0, pvProduction = 0 } = useMemo(
        () => getNetTotalEnergyConsumption(postRetrofitResultsData[postRetrofitUpgradeCaseKey] || {}),
        [postRetrofitResultsData]
    );

    const { annualEnergyConsumption: aecBeforeRetrofit = 0 } = useMemo(
        () => getNetTotalEnergyConsumption(preRetrofitResultsData["socBaseCase"] || {}),

        [preRetrofitResultsData]
    );

    const typicalNewHome = 98;

    const energyReduction = useMemo(() => {
        const { zeroFloorTotal: netTotalConsumption = 0 } = getNetTotalEnergyConsumption(
            postRetrofitResultsData[upgradeCaseKey] || {}
        );
        const { zeroFloorTotal: netBaseConsumption = 0 } = getNetTotalEnergyConsumption(
            preRetrofitResultsData["socBaseCase"] || {}
        );

        return ((100 * (netBaseConsumption - netTotalConsumption)) / netBaseConsumption).toFixed(1);
    }, [preRetrofitResultsData, postRetrofitResultsData]);

    // const operatingCostSaving = useMemo(() => {
    //     const operatingExpenses = calcOperatingCosts(postRetrofitResultsData[upgradeCaseKey], fuelPrices, "total");
    //     const baseOperatingExpenses = calcOperatingCosts(preRetrofitResultsData["socBaseCase"], fuelPrices, "total");

    //     return baseOperatingExpenses - operatingExpenses;
    // }, [fuelPrices, preRetrofitResultsData, postRetrofitResultsData]);

    const calculateOperatingCostSaving = () => {
        const operatingExpenses = calcOperatingCosts(postRetrofitResultsData[upgradeCaseKey], fuelPrices, "total");
        const baseOperatingExpenses = calcOperatingCosts(preRetrofitResultsData["socBaseCase"], fuelPrices, "total");

        const savings = baseOperatingExpenses - operatingExpenses;

        return parseFloat(savings.toFixed(0));
    };

    useEffect(() => {
        if (operatingCostSavingOverride) return;

        const savings = calculateOperatingCostSaving();
        updateNzStudyPdfData("operatingCostSaving", savings);
    }, []);

    // const { socBaseCase: { resultsSummary: { annualEmissions: preAnnualEmissions = 0 } = {} } = {} } =
    //     preRetrofitResultsData || {};
    // const { [postRetrofitUpgradeCaseKey]: { resultsSummary: { annualEmissions: postAnnualEmissions = 0 } = {} } = {} } =
    //     postRetrofitResultsData || {};

    const preRetrofitEmissions = preRetrofitModelDataFromResults?.program?.results?.tsv?.ERSGHG || 0;

    const postRetrofitEmissions = postRetrofitUpgradeCaseKey.includes("BaseCase")
        ? postRetrofitModelDataFromResults?.program?.results?.tsv?.ERSGHG || 0
        : postRetrofitModelDataFromResults?.program?.results?.tsv?.UGRERSGHG || 0;

    const beforeRenovationData = useMemo(() => {
        const {
            Annual: {
                Consumption: {
                    SpaceHeating: { _attributes: { total: totalSpaceHeating = 0 } = {} } = {},
                    HotWater: { _attributes: { primary: primaryDHW = 0 } = {} } = {},
                    Electrical: {
                        _attributes: {
                            baseload = 0,
                            ventilation = 0,
                            spaceCooling = 0,
                            airConditioning = 0,
                            appliance = 0,
                            heatPump = 0,
                            lighting = 0,
                            // total: totalConsumption = 0,
                        } = {},
                    } = {},
                } = {},
            } = {},
        } = preRetrofitResultsData["socBaseCase"] || {};

        return {
            spaceHeating: totalSpaceHeating,
            spaceCooling,
            waterHeating: primaryDHW,
            ventilation: ventilation,
            lightAndAppliances: baseload,
            otherElectrical: airConditioning + appliance + heatPump + lighting,
            // totalConsumption,
        };
    }, [preRetrofitResultsData]);

    const afterRenovationData = useMemo(() => {
        const {
            Annual: {
                Consumption: {
                    SpaceHeating: { _attributes: { total: totalSpaceHeating = 0 } = {} } = {},
                    HotWater: { _attributes: { primary: primaryDHW = 0 } = {} } = {},
                    Electrical: {
                        _attributes: {
                            baseload = 0,
                            ventilation = 0,
                            spaceCooling = 0,
                            airConditioning = 0,
                            appliance = 0,
                            heatPump = 0,
                            lighting = 0,
                            // total: totalConsumption = 0,
                        } = {},
                    } = {},
                } = {},
            } = {},
        } = postRetrofitResultsData[postRetrofitUpgradeCaseKey] || {};

        return {
            spaceHeating: totalSpaceHeating,
            spaceCooling,
            waterHeating: primaryDHW,
            ventilation: ventilation,
            lightAndAppliances: baseload,
            otherElectrical: airConditioning + appliance + heatPump + lighting,
            // totalConsumption,
        };
    }, [postRetrofitResultsData]);

    return (
        <div className={classes.innerPageContainer}>
            <InputSectionContainer title={"Annual Energy Use"}>
                <div className={classes.flexRow}>
                    <Input
                        label={"Before Retrofit"}
                        input={{
                            value: `${aecBeforeRetrofit.toFixed(0)} GJ/year`,
                        }}
                        disabled
                    />
                    <Input
                        label={"Typical New Home"}
                        input={{
                            value: `${typicalNewHome.toFixed(0)} GJ/year`,
                        }}
                        disabled
                    />
                    <Input
                        label={"After Retrofit (Pre-PV)"}
                        input={{
                            value: `${aecPostRetrofit.toFixed(0)} GJ/year`,
                        }}
                        disabled
                    />
                    <Input
                        label={"PV Production"}
                        input={{
                            value: `${(aecPostRetrofit - pvProduction).toFixed(0)} GJ/year`,
                        }}
                        disabled
                    />
                </div>
            </InputSectionContainer>

            <InputSectionContainer title={"Energy Consumption Breakdown"}>
                <div className={classNames(classes.flexRow, classes.pieCharts)}>
                    <div className={classes.chartContainer}>
                        Before Renovation
                        <PieChartSVG width={225} height={225} rawData={beforeRenovationData} />
                    </div>
                    <div className={classes.chartContainer}>
                        After Renovation
                        <PieChartSVG width={175} height={175} rawData={afterRenovationData} />
                    </div>
                    <div
                        style={{
                            display: "flex",
                            flexDirection: "column",
                            margin: "0 auto",
                        }}
                    >
                        {Object.entries(sectionColorLabel).map(([key, { color, label }]) => (
                            <div
                                key={key}
                                style={{
                                    display: "flex",
                                    alignItems: "center",
                                    marginBottom: "5px",
                                    flex: "50%",
                                }}
                            >
                                <div
                                    style={{
                                        width: "10px",
                                        height: "10px",
                                        background: color,
                                        marginRight: "10px",
                                        border: "1px solid #636365",
                                    }}
                                />
                                <span
                                    style={{
                                        fontSize: "14px",
                                    }}
                                >
                                    {label}
                                </span>
                            </div>
                        ))}
                    </div>
                </div>
            </InputSectionContainer>

            <InputSectionContainer title={"As Built Energy Performance"}>
                <div className={classes.energyPerformanceRow1}>
                    <Input
                        label={"Annual Energy Savings"}
                        input={{
                            value: `${energyReduction}%`,
                        }}
                        disabled
                    />
                    <Input
                        label={"Greenhouse Gas Savings"}
                        input={{
                            value: `${(preRetrofitEmissions - postRetrofitEmissions)?.toFixed(2) || 0.0} Tonnes`,
                        }}
                        disabled
                    />
                    <div style={{ display: "flex", alignItems: "flex-end", gap: "8px", flex: 1 }}>
                        <Input
                            type={"number"}
                            label={"Estimated Cost Savings ($/y)"}
                            // input={{
                            //     value: `$${operatingCostSaving?.toFixed(2) || 0.0}/y`,
                            // }}
                            input={{
                                value: operatingCostSaving,
                                onChange: (e) => {
                                    updateNzStudyPdfData("operatingCostSaving", parseFloat(e.toFixed(0))),
                                        updateNzStudyPdfData("operatingCostSavingOverride", true);
                                },
                            }}
                        />
                        <button
                            onClick={() => {
                                const calculatedValue = calculateOperatingCostSaving();
                                updateNzStudyPdfData("operatingCostSaving", calculatedValue);
                                updateNzStudyPdfData("operatingCostSavingOverride", false);
                            }}
                            style={{
                                background: "transparent",
                                border: "none",
                                cursor: "pointer",
                                padding: "4px",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                marginBottom: "20px",
                                flexShrink: 0,
                            }}
                            title="Reset to calculated value"
                        >
                            <svg
                                width="16"
                                height="16"
                                viewBox="0 0 24 24"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                                style={{ color: "#626261" }}
                            >
                                <path
                                    d="M1 4V10H7"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                />
                                <path
                                    d="M23 20V14H17"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                />
                                <path
                                    d="M20.49 9A9 9 0 0 0 5.64 5.64L1 10M23 14L18.36 18.36A9 9 0 0 1 3.51 15"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                />
                            </svg>
                        </button>
                    </div>
                </div>
                <div className={classes.energyPerformanceRow2}>
                    <Input
                        type={"number"}
                        label={"Estimated Upfront (Embodied) Carbon from Retrofit (Tonnes)"}
                        minHeight={"100px"}
                        input={{
                            value: estimatedUpfront,
                            onChange: (e) => updateNzStudyPdfData("estimatedUpfront", e),
                        }}
                    // className={classes.upfrontCarbon}
                    />
                    <Input
                        type={"number"}
                        label={"Carbon Payback (years)"}
                        minHeight={"100px"}
                        input={{ value: carbonPayback, onChange: (e) => updateNzStudyPdfData("carbonPayback", e) }}
                        className={classes.upfrontCarbon}
                    />
                </div>
            </InputSectionContainer>
        </div>
    );
};

const mapStateToProps = createStructuredSelector({
    nzStudyReportPdfData: selectNzStudyReportPdfData,
    results: selectResults,
    fuelPrices: selectFuelPrices,
});

const mapDispatchToProps = (dispatch) => ({
    updateNzStudyPdfData: (fieldName, value) => dispatch(updateNzStudyPdfData(fieldName, value)),
});

export default connect(mapStateToProps, mapDispatchToProps)(EnergySummary);
